import { isRouteErrorResponse, useRouteError } from "react-router";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";

export function ErrorBoundary() {
  const error = useRouteError();

  let errorMessage = "An unexpected error occurred";
  let errorDetails = "We're sorry for the inconvenience. Please try again.";
  let statusCode = 500;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    errorMessage = error.status === 404 ? "Page Not Found" : "Server Error";
    errorDetails =
      error.status === 404
        ? "The page you're looking for doesn't exist."
        : error.statusText || errorDetails;
  } else if (error instanceof Error) {
    errorMessage = "Application Error";
    errorDetails = error.message;

    // Log error details in development
    if (import.meta.env.DEV) {
      console.error("Error boundary caught:", error);
    }
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-2xl font-sans text-[#431908]">
            {statusCode} - {errorMessage}
          </CardTitle>
          <CardDescription className="text-[#431908] opacity-70">
            {errorDetails}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
            <Button onClick={handleGoHome} className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              Go to Dashboard
            </Button>
          </div>

          {import.meta.env.DEV && error instanceof Error && (
            <details className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
              <summary className="cursor-pointer font-medium text-gray-700">
                Error Details (Development Only)
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600 overflow-auto">
                {error.stack}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
